"use client"

const SESSION_STORAGE_KEY = "phantomcode.observability.session_id"
const VISITOR_STORAGE_KEY = "phantomcode.observability.visitor"
const NEW_VISITOR_SESSION_KEY = "phantomcode.observability.new_visitor"
const SESSION_COOKIE = "pc_observability_sid"

type VisitorRecord = {
  id: string
  first_seen_at: string
}

function randomHex(bytes: number): string {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const values = new Uint8Array(bytes)
    crypto.getRandomValues(values)
    return Array.from(values, (value) => value.toString(16).padStart(2, "0")).join("")
  }
  let value = ""
  while (value.length < bytes * 2) {
    value += Math.floor(Math.random() * 0x100000000)
      .toString(16)
      .padStart(8, "0")
  }
  return value.slice(0, bytes * 2)
}

export function generateBrowserTraceId(): string {
  return randomHex(16)
}

export function generateBrowserSpanId(): string {
  return randomHex(8)
}

export function getBrowserSessionId(): string {
  if (typeof window === "undefined") return generateBrowserTraceId()
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (existing && /^[a-f0-9]{32}$/i.test(existing)) return existing
    const created = generateBrowserTraceId()
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, created)
    document.cookie = `${SESSION_COOKIE}=${created}; Path=/; Max-Age=86400; SameSite=Lax; Secure`
    return created
  } catch {
    return generateBrowserTraceId()
  }
}

export function getBrowserVisitor(): {
  anonymous_id: string
  visitor_type: "new" | "returning"
  visitor_first_seen_at: string
} {
  const fallback = {
    anonymous_id: generateBrowserTraceId(),
    visitor_type: "new" as const,
    visitor_first_seen_at: new Date().toISOString(),
  }
  if (typeof window === "undefined") return fallback

  try {
    const raw = window.localStorage.getItem(VISITOR_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as VisitorRecord
      if (parsed.id && parsed.first_seen_at) {
        return {
          anonymous_id: parsed.id,
          visitor_type:
            window.sessionStorage.getItem(NEW_VISITOR_SESSION_KEY) === "1"
              ? "new"
              : "returning",
          visitor_first_seen_at: parsed.first_seen_at,
        }
      }
    }

    const created: VisitorRecord = {
      id: generateBrowserTraceId(),
      first_seen_at: new Date().toISOString(),
    }
    window.localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(created))
    window.sessionStorage.setItem(NEW_VISITOR_SESSION_KEY, "1")
    return {
      anonymous_id: created.id,
      visitor_type: "new",
      visitor_first_seen_at: created.first_seen_at,
    }
  } catch {
    return fallback
  }
}

export function buildTraceHeaders(traceId = generateBrowserTraceId()): {
  traceId: string
  spanId: string
  headers: Record<string, string>
} {
  const spanId = generateBrowserSpanId()
  return {
    traceId,
    spanId,
    headers: {
      "x-trace-id": traceId,
      "x-session-id": getBrowserSessionId(),
      traceparent: `00-${traceId}-${spanId}-01`,
    },
  }
}
